// Bento layout policy (TASK-493, reworked by TASK-601). The single knob for "make
// the canvas read right": every registered widget declares a tile size; each size
// maps to grid-span classes AND a max-height cap (overflow scrolls inside the tile).
// A spec may override with an explicit `size`; legacy names are normalized.
import type { LegacyTileSize, TileSize, WidgetSpec } from './types';

// TASK-601 size table — resist anything fancier:
//   sm   1 col · 280px  — KPI/score cards
//   md   2 col · 420px  — lists, narratives, medium charts
//   lg   full  · 560px  — dashboards, tables, chart+table composites
//   tall 2 col · 560px  — timelines/long lists, scroll inside
// Every registry key gets an explicit entry (tileLayout.test.ts enforces this);
// anything unlisted (future widgets) falls back to "md".
export const DEFAULT_SIZE: Record<string, TileSize> = {
  // Full-row dashboards / composites
  ChangeRadar: 'lg',
  CockpitPortfolioView: 'lg',
  DocumentsTab: 'lg',
  DocumentComplianceDashboard: 'lg',
  RegulatoryFeedTab: 'lg',
  PortfoliosTab: 'lg',
  ClientLevelView: 'lg',
  MeetingPrep: 'lg',
  Client360: 'lg',
  EmailDraft: 'lg',
  BeforeAfter: 'lg',
  // TASK-831: a per-holding list with a vintage header — tall, like DriftBars.
  CIOHouseView: 'tall',
  // TASK-830: one subject with its evidence and replacements — a column, not a wall.
  DnaConflict: 'tall',
  // TASK-833: a header, an embedded evidence widget and a footer — the same column,
  // one frame taller because it wraps one of the others.
  SignalDossier: 'tall',
  ComplianceCockpit: 'lg',
  KYCTab: 'lg',
  RiskProfileTab: 'lg',
  RiskAnalytics: 'lg',
  AllDocumentsView: 'lg',
  DocumentAnalysisTab: 'lg',
  DocumentViewerPanel: 'lg',
  SecureLinksTable: 'lg',
  // Vertical lists / timelines — 2 cols, scroll inside
  DnaCard: 'tall',
  NotesTab: 'tall',
  MinIODocumentsSection: 'tall',
  ProposalsList: 'tall',
  QuarantineQueue: 'tall',
  COReviewQueue: 'tall',
  COReviewWorkspace: 'lg', // TASK-733 — sidebar + section viewer + comments need the full row
  ClientBook: 'lg', // 5-column table + reason lines — needs the full row (audit follow-up)
  MeetingsList: 'lg', // TASK-666 — 5-column table + multi-badge record column
  // TASK-808 — subject + sender + snippet per row, and a row expands to a full message body
  // in place. A narrow tile would truncate every subject and make the list unreadable.
  EmailList: 'lg',
  LiveMeetingNow: 'md', // TASK-667 — must stay short enough to sit beside a Teams window
  // Deep review #4: both were registered without a size, so the epic's two canvas-first
  // surfaces silently fell through to 'md'. The recorder genuinely wants 'md' (timer + level
  // meter, nothing else); the review tile embeds the whole PostMeetingPage and needs the row.
  MeetingRecorderWidget: 'md', // TASK-665/673
  PostMeetingReviewWidget: 'lg', // TASK-662 pass 3 — post-meeting review as a tile
  DriftBars: 'tall',
  // TASK-901: verdict strip + 12-slot table + trade rows — a composite, so full row.
  TradeDelta: 'lg',
  ConflictsList: 'tall',
  SectorTreemap: 'tall',
  ActivityTimeline: 'tall',
  // Medium content — 2 cols so charts/text get real width
  LifeEvents: 'md',
  NewProspect: 'md',        // TASK-659
  OnboardingChecklist: 'md', // TASK-659
  AllocationDonut: 'md',
  ClientProfileSummary: 'md',
  PortfolioCommentary: 'md',
  RiskCommentary: 'md',
  ResearchBrief: 'lg',  // TASK-612
  IndustryDossier: 'lg',  // TASK-612
  MorningDigest: 'lg',  // TASK-611
  // TASK-685 — a short list of titles + one action each; it is a queue, not a table.
  TasksAwaitingReview: 'md',
  // TASK-758 — a short list of broken pointers; usually empty, never a full-width panel.
  ArtifactRepairQueue: 'md',
  // TASK-687 — an input box, a stance, and a few quoted drivers; a narrative, not a table.
  ClientReaction: 'md',
  // TASK-688 — a date axis needs width; the step chart is unreadable in a 2-col tile.
  RiskTimeline: 'lg',
  // TASK-689 — a ranked list of questions; reads like a checklist, scrolls inside.
  KnowledgeGaps: 'tall',
  // TASK-690 — a 4-column table with bars; needs the full row like the other breakdowns.
  CountryExposure: 'lg',
  // A five-column table of values — full width, like every other dense table.
  PositionsTable: 'lg',
  IssuerConcentration: 'lg',
  SecurityExposure: 'lg',
  MaturityLadder: 'lg',
  ComplianceReview: 'md',
  MeetingActionItems: 'md',
  DocumentRequestList: 'md',
  AIUsageStats: 'md',
  ChatMessage: 'md', // rendered BARE (col-span-full, no chrome) — entry kept for completeness
  // Compact cards
  SuitabilityCard: 'sm',
  ArtifactCard: 'sm', // TASK-742 — pointer card: icon, title, three actions
  RiskProfileSummary: 'sm',
  ComplianceStatus: 'sm',
  VoiceNoteWidget: 'sm',
};

// Span classes enumerated as full static strings so the Tailwind JIT keeps them.
// USER DECISION (2026-07-19, TASK-600 review): every widget opens at MAXIMUM
// width — all sizes span the full row. The size classes stay as metadata (the
// registry annotation survives) so per-size layouts can return with a one-line
// change here if the call is ever reversed.
export const SPAN_CLASS: Record<TileSize, string> = {
  sm: 'col-span-full',
  md: 'col-span-full',
  lg: 'col-span-full',
  tall: 'col-span-full',
};

// Max tile-body height — MAXIMUM height per the same decision: up to 80% of the
// viewport, overflow scrolls INSIDE the tile (never the page). max-h, not fixed:
// short content stays short.
export const MAXH_CLASS: Record<TileSize, string> = {
  sm: 'max-h-[80vh]',
  md: 'max-h-[80vh]',
  lg: 'max-h-[80vh]',
  tall: 'max-h-[80vh]',
};

// Pre-TASK-601 size names still accepted from any older spec source.
const LEGACY_SIZE: Record<string, TileSize> = { standard: 'sm', wide: 'lg' };

export function normalizeSize(size: LegacyTileSize): TileSize {
  return (LEGACY_SIZE[size] ?? size) as TileSize;
}

export function resolveSize(spec: WidgetSpec): TileSize {
  if (spec.size) return normalizeSize(spec.size);
  return DEFAULT_SIZE[spec.component] ?? 'md';
}

// Widgets that render their OWN bordered/padded card surface(s). CanvasTile must
// NOT add its own padding for these or they read as double-framed.
export const SELF_CONTAINED = new Set<string>([
  'ChangeRadar',
  // TASK-908 / TASK-906: render their own WidgetContainer.
  'PositionsTable',
  'IssuerConcentration',
  'SecurityExposure',
  'MaturityLadder',
  // TASK-494: every adapter wraps its widget in WidgetContainer (own card), so
  // CanvasTile must not add padding — it would double-frame.
  'CockpitPortfolioView',
  'DnaCard',
  'NotesTab',
  'DocumentsTab',
  'MinIODocumentsSection',
  'PortfoliosTab',
  'ProposalsList',
  'COReviewQueue',
  'COReviewWorkspace', // TASK-733 — adapter renders its own WidgetContainer card
  'DocumentComplianceDashboard',
  'QuarantineQueue',
  'RegulatoryFeedTab',
  // TASK-495: ClientBook + ClientLevelWidget render their own WidgetContainer card.
  'ClientBook',
  'ClientLevelView',
  // TASK-496: ports render their own WidgetContainer card.
  'MeetingPrep',
  'DriftBars',
  'TradeDelta',
  'AllocationDonut',
  'ConflictsList',
  'Client360',
  'SectorTreemap',
  'EmailDraft',
  'BeforeAfter',
  // TASK-831: renders its own WidgetContainer, like the other ported widgets.
  'CIOHouseView',
  'DnaConflict',
  // TASK-833: renders its own WidgetContainer AND embeds a widget that renders one.
  'SignalDossier',
  // TASK-808 — EmailList renders its own WidgetContainer (own file, like ClientBook), so
  // CanvasTile must not add padding around it.
  'EmailList',
]);
